import { el, setChildren } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import SingleSelectNative from 'generics/components/single-select/native';
import SaveButton from 'generics/components/save-button';

import { findIndexByPropertyValue, getPropertyValues } from 'utilities/collection';
import { hasSameContents } from 'utilities/array';

import HelptapEditor from '../../../../helpers';

import './index.scss';

const duplicateTypes = {
  WITHOUT_SYNC: 'WITHOUT_SYNC',
  WITH_SYNC: 'WITH_SYNC'
};

const positionTypes = {
  TOP: 'TOP',
  ABSOLUTE: 'ABSOLUTE',
  BOTTOM: 'BOTTOM'
};

const SELECT_ALL = 'SELECT_ALL';

class AdvancedDuplicatePanelContent {

  constructor(configuration) {

    this.allPagesSelected = false;
    this.duplicateType = duplicateTypes.WITHOUT_SYNC;
    this.positionType = positionTypes.TOP;

    this.editor = configuration.editor;
    this.node = configuration.node;
    this.activeNodePath = configuration.activeNodePath;
    this.currentPageId = configuration.currentPageId;
    this.onComplete = configuration.onComplete;
    this.selectedPageIds = [this.currentPageId];
    this.menu = configuration.menu;

    this.duplicateTypeInput = null;
    this.pagesSelectInput = null;
    this.positionTypeInput = null;

    this.el = el('#advanced-duplicate-panel-content-main', [el('ht-spinner', { size: 'small' })]);

  }

  buildDuplicateTypeInputOptions() {

    const options = {
      placeholder: 'How you want to duplicate?',
      options: [{
        label: 'Without sync',
        value: duplicateTypes.WITHOUT_SYNC
      }, {
        label: 'With sync',
        value: duplicateTypes.WITH_SYNC
      }],
      selectedOptionIndices: [0],
      onChange: this.handleDuplicateTypeSelect.bind(this)
    };

    return options;

  }

  buildPagesSelectInputOptions() {

    const options = {
      multiSelect: true,
      onChange: this.handlePagesSelectInputChange.bind(this),
      placeholder: 'Select pages',
      labelFieldName: 'path',
      valueFieldName: '_id',
      options: [...this.pages],
      selectedOptionIndices: [findIndexByPropertyValue(this.pages, '_id', this.currentPageId)]
    };

    return options;

  }

  buildPagesSelectInputOptionsWithAllPagesSelected() {

    const options = this.buildPagesSelectInputOptions();

    const selectedOptionIndices = [];
    const allOptions = options.options;
    const allOptionsLength = allOptions.length;

    for (let index = 0; index < allOptionsLength; index++) {
      selectedOptionIndices.push(index);
    }

    options.selectedOptionIndices = selectedOptionIndices;

    return options;

  }

  buildPagesSelectInputOptionsWithSelectedPagesSelected() {

    const options = this.buildPagesSelectInputOptions();

    const selectedOptionIndices = [];
    const selectOptions = options.options;
    const selectedPageIds = this.selectedPageIds;
    const selectedPageIdsLength = selectedPageIds.length;

    for (let index = 0; index < selectedPageIdsLength; index++) {
      const selectedPageId = selectedPageIds[index];
      const selectedPageIdIndex = findIndexByPropertyValue(selectOptions, '_id', selectedPageId);
      selectedOptionIndices.push(selectedPageIdIndex);
    }

    options.selectedOptionIndices = selectedOptionIndices;

    return options;

  }

  buildPositionTypeOptions() {

    const options = {
      placeholder: 'Where do you want to position?',
      options: [{
        label: 'Top',
        value: positionTypes.TOP
      }, {
        label: 'Bottom',
        value: positionTypes.BOTTOM
      }, {
        label: 'Absolute',
        value: positionTypes.ABSOLUTE
      }],
      selectedOptionIndices: [0],
      onChange: this.handlePositionTypeInputChange.bind(this)
    };

    return options;

  }

  renderInputs() {

    this.duplicateTypeInput = new SingleSelectNative(this.buildDuplicateTypeInputOptions());
    this.pagesSelectInput = new SingleSelectNative(this.buildPagesSelectInputOptions());
    this.positionTypeInput = new SingleSelectNative(this.buildPositionTypeOptions());

    return el('#advanced-duplicate-panel-content-inputs', [
      el('.form-input-row', [el('label.side-value-control-label.field-label', 'Sync'), this.duplicateTypeInput]),
      el('.form-input-row', [el('label.side-value-control-label.field-label', 'Select pages'), this.pagesSelectInput]),
      el('.form-input-row', [el('label.side-value-control-label.field-label', 'Position'), this.positionTypeInput])
    ]);

  }

  renderButtons() {

    const saveButton = new SaveButton({
      label: 'Save',
      onClick: this.handleSaveButtonClick.bind(this)
    });

    return el('#advanced-duplicate-panel-content-buttons', [
      saveButton
    ]);

  }

  handleDuplicateTypeSelect({ values }) {

    this.duplicateType = values[0];

  }

  getAllPageIds() {

    const allPageIds = getPropertyValues(this.pages, '_id');

    return allPageIds;

  }

  isAllPagesSelected() {

    return this.selectedPageIds.length === this.pages.length;

  }

  handlePagesSelectInputChange({ values }) {

    this.selectedPageIds = values;

  }

  handlePositionTypeInputChange({ values }) {

    this.positionType = values[0];

  }

  isCurrentPageSelected() {

    return this.selectedPageIds.indexOf(this.currentPageId) !== -1;

  }

  getInsertPath() {

    if (this.positionType === positionTypes.TOP) {
      return [0];
    } else if (this.positionType === positionTypes.ABSOLUTE) {
      return this.activeNodePath;
    } else {
      return [this.editor.children.length];
    }

  }

  insertSynchronizedPageNodeInCurrentPage(synchronizedPageRegion) {

    const synchronizedPageRegionNode = HelptapEditor.synchronizedPageRegion.buildNode(synchronizedPageRegion);

    HelptapEditor.insertNodes(this.editor, synchronizedPageRegionNode, { at: this.getInsertPath() });
    HelptapEditor.deleteNodeByPath(this.editor, this.activeNodePath);

  }

  duplicateNodeInCurrentPage() {

    try {

      HelptapEditor.duplicate.duplicateNode(this.editor, this.node, this.getInsertPath());

      this.onComplete();
    } catch (exception) {

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: exception.message,
        seconds: 5,
        type: 'error'
      });

    }

  }

  async duplicateNodeInOtherPages() {

    const fullscreenBanner = application.prompts.displayFullScreenBanner('Please wait... Duplicating node to selected pages');

    await serverInterface.startup.pageEditorActions.duplicateNodeToPages({
      sourcePageId: this.currentPageId,
      destinationPageIds: this.selectedPageIds,
      node: this.node,
      position: {
        type: this.positionType,
        path: this.activeNodePath
      }
    });

    fullscreenBanner.destroy();

    this.onComplete();

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Block is duplicated in selected pages',
      seconds: 5,
      type: 'info'
    });

  }

  async synchronizeNodeInCurrentPage() {

    const synchronizedPageRegion = await serverInterface.startup.synchronizedPageRegions.createSynchronizedPageRegion({
      pageId: this.currentPageId,
      nodes: [this.node]
    });

    this.onComplete();

    this.insertSynchronizedPageNodeInCurrentPage(synchronizedPageRegion);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Block is synchronized!',
      seconds: 5,
      type: 'info'
    });

  }

  displaySynchronizeNodeInOtherPagesException(exception) {

    let message = 'Unknown error. Contact administrator';

    if (exception.code === 'UNABLE_TO_CLONE_NODE') {
      message = 'Unable to synchronize! Cloning block failed. Contact administrator';
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message,
      seconds: 5,
      type: 'error'
    });

  }

  async synchronizeNodeInOtherPages() {

    const fullscreenBanner = application.prompts.displayFullScreenBanner('Please wait... Synchronizing node to selected pages');

    try {

      const result = await serverInterface.startup.pageEditorActions.insertSynchronizedPageNodeInPages({
        sourcePageId: this.currentPageId,
        destinationPageIds: this.selectedPageIds,
        nodes: [this.node],
        position: {
          type: this.positionType,
          path: this.activeNodePath
        }
      });

      this.onComplete();

      if (this.isCurrentPageSelected() === true) {
        this.insertSynchronizedPageNodeInCurrentPage(result.synchronizedPageRegion);
      }

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Added synchronized block in all selected pages!',
        seconds: 5,
        type: 'info'
      });

    } catch (exception) {

      this.displaySynchronizeNodeInOtherPagesException(exception);

    } finally {
      fullscreenBanner.destroy();
    }

  }

  async handleSaveButtonClick() {

    if (this.selectedPageIds === 0) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Select at least one page to duplicate',
        seconds: 5,
        type: 'error'
      });
    }

    if (this.duplicateType === duplicateTypes.WITHOUT_SYNC) {
      if (this.selectedPageIds.length === 1 && this.selectedPageIds[0] === this.currentPageId) {
        this.duplicateNodeInCurrentPage();
      } else {
        await this.duplicateNodeInOtherPages();
      }
    } else {
      if (this.selectedPageIds.length === 1 && this.selectedPageIds[0] === this.currentPageId) {
        await this.synchronizeNodeInCurrentPage();
      } else {
        await this.synchronizeNodeInOtherPages();
      }
    }

  }

  async loadPages() {

    const queryOptions = {
      filterOptions: {},
      projection: ['_id', 'path']
    };

    this.pages = await serverInterface.startup.pages.getAllActivePages(queryOptions);

  }

  async initialize() {

    await this.loadPages();

    setChildren(this.el, [
      this.renderInputs(),
      this.renderButtons()
    ]);

    this.menu.repositionMenu();

  }

  onmount() {

    this.initialize();

  }

}

export default AdvancedDuplicatePanelContent;